export const PROJECTS = [

  {
    id: '01',
    slug: 'magic-mixies',
    title: 'Magic Mixies',
    category: 'Global Multi Sub-Brand Identity Systems',
    client: 'Moose Toys',
    role: 'Art Director · Creative Lead',
    hue: '270',
    stat: 'Back-to-back TOTY Creative Toy of the Year',
    awards: [
      'TOTY Creative Toy of the Year — Magic Cauldron, 2022',
      'TOTY Creative Toy of the Year — Magical Crystal Ball, 2023',
      'TOTY Finalist — Doll of the Year, Pixie Supremes, 2025',
    ],
    heroImage: '/projects/magic-mixies/hero.webp',
    heroVideo: '/projects/magic-mixies/magicMixiesTVC.mp4',
    images: [],
    overview:
      'Magic Mixies is one of the most successful toy franchises Moose Toys has ever built — and it was founded on a single, uncommonly good idea: put something alive inside a vessel, hide it in real mist, and let a child discover it through the act of casting a spell.\n\nThe Magic Cauldron launched in October 2021 and immediately became the highest-grossing SKU per week in Moose Toys\' history, outselling Shopkins at its peak. Within its first week nationally, it sold nearly 100,000 units. By December it held the No. 1 position in Special Feature Plush across the US (NPD Group). The #MagicMixies hashtag surpassed 817 million views on TikTok without a single paid post driving it — the product was the content.\n\nOver the following four years, that founding idea was extended into five distinct sub-brands spanning interactive plush, miniature collectibles, fashion dolls, and premium play sets. Each one was a separate creative brief. Each one had to honour the original while building something new. My role across all of them was brand system, art direction, and campaign — from the first sketch to the retail shelf across 50+ markets.',
    brief:
      'The launch brief was deceptively simple: don\'t explain the magic. Every other toy tells kids what it does. Magic Mixies had to show it — the reveal was the entire campaign, and the job was to not get in the way of it.\n\nAs the franchise grew, each sub-brand carried its own version of that brief: how do you apply a reveal mechanic to a completely different format — collectible blind bags, a doll in a bottle, a premium fashion set — without losing what makes Magic Mixies feel special? The answer was always the same: build everything around the moment of discovery.',
    direction:
      'Theatre over explanation. The creative direction across every sub-brand was built on a single principle: anticipation is the product. The mist, the darkened room, the glowing vessel — these weren\'t production choices, they were the brand.\n\nVisual language: atmospheric, dark-lit, and cinematic. Child-centred without being infantilised. The aesthetic drew from stage magic and fairytale illustration — deliberately far from the bright-white-and-primary-colours of conventional toy photography. Every image had to look like something worth pausing for.\n\nFor sub-brands with different formats — the fashion dolls of Pixlings, the miniature world of Mixlings — the challenge was adapting that visual language to a new product truth while keeping the family resemblance visible.',
    execution:
      'Full brand systems developed and maintained across six years of franchise activity. Art direction scope: TVC, digital campaign, retail POS, trade show creative (Toy Fair New York, Nuremberg Spielwarenmesse, Hong Kong, Melbourne Toy Fair), and social content.\n\nEach sub-brand launch required its own identity system built inside the Magic Mixies umbrella — enough distinctiveness to stand alone on shelf, enough connection to carry the franchise equity. Style guides developed for global teams across 50+ markets.',
    results:
      'Magic Cauldron ranked No. 1 in Special Feature Plush for Fall 2021 (NPD Group). Magical Crystal Ball held No. 1 in October 2022. At peak, Moose Toys held 46% dollar share of the entire Special Feature Plush category — and the No. 1, 2, 3, and 4 spots simultaneously. Magic Mixies drove Plush to become the fastest-growing toy supercategory with +27% YoY growth Q3 2021 to Q3 2022. The franchise won back-to-back TOTY Creative Toy of the Year awards and generated over 817 million TikTok views organically.',
    tags: ['Umbrella Brand', 'Brand System', 'Campaign', 'TV', 'Retail POS', 'Global', 'Collectibles', 'Fashion Dolls', 'TOTY Winner'],
    subBrands: [
      {
        id: '01',
        name: 'Magic Mixies Magic Cauldron',
        year: 'Launched 2021',
        color: '#7B3FE4',
        awards: ['TOTY Creative Toy of the Year, 2022'],
        blurb: 'The one that started everything. A misting cauldron playset where kids follow a spell — adding ingredients, waving a wand — until the vessel fills with real mist and a hidden interactive plush Mixie is revealed inside. It became the highest-grossing SKU per week in Moose Toys history at launch, outselling Shopkins. Nearly 100,000 units sold in a single week nationally. No. 1 on Amazon throughout October 2021. No. 1 in Special Feature Plush for Fall 2021 (NPD Group). The #MagicMixies TikTok tag surpassed 817 million views — entirely organic. Won TOTY Creative Toy of the Year at the 22nd annual awards in February 2022.',
        imageFolder: 'cauldron',
      },
      {
        id: '02',
        name: 'Magic Mixies Magical Crystal Ball',
        year: 'Launched 2022',
        color: '#5B8CFF',
        awards: ['TOTY Creative Toy of the Year, 2023'],
        blurb: 'The Cauldron proved the mechanic. The Crystal Ball proved the franchise. Kids used a spellcasting wand to fill the crystal ball with mist and create a Mixie inside — adding fortune-telling, spell, game, and tickling play modes after the reveal. It held No. 1 in Special Feature Plush in October 2022. At its peak, Moose Toys held 46% dollar share of the entire category. Won TOTY Creative Toy of the Year in September 2022 — back-to-back wins for the Magic Mixies brand.',
        imageFolder: 'crystal-ball',
        reverseLayout: true,
      },
      {
        id: '03',
        name: 'Magic Mixies Mixlings',
        year: 'Launched 2022',
        color: '#FF6B9D',
        awards: [],
        blurb: 'Moose Toys\' entry into the collectibles category — built on Magic Mixies equity. Mini blind-pack figures in mystery packs, each with a distinct magical power type: Glowlings that glow in the dark, Lucklings that reveal fortunes, Loxies with hair reveals, Aqua Chanjas that colour-change, and Flutterfliers with wings. Series 1 launched with 40+ unique characters. Followed by a Collector\'s Cauldron playset, a Magic Castle set, and multiple series through to 2024\'s Shimmerverse wave. The brief was to bring the Magic Mixies sense of discovery to a collectible format — and make the blind-pack reveal feel like a scaled-down version of the original spell.',
        imageFolder: 'mixlings',
      },
      {
        id: '04',
        name: 'Magic Mixies Pixlings',
        year: 'Launched 2023',
        color: '#C84FD8',
        awards: [],
        blurb: 'Magic Mixies enters the fashion doll category — a first for Moose Toys. Kids create a doll inside a potion bottle by adding water, coloured elixirs, and ingredients, then pressing the crystal gem stopper to turn the potion transparent and reveal a fully-dry, posable 6-inch doll inside. Characters included Marena the Mermaid, Deerlee the Deer, and Unia the Unicorn at launch. Described by Kidscreen at launch as "poised to disrupt the dolls category." The creative brief was to make the reveal mechanic work in a totally different product format — and to build a fashion doll identity that stood on its own while carrying the Magic Mixies family resemblance.',
        imageFolder: 'pixlings',
        reverseLayout: true,
      },
      {
        id: '05',
        name: 'Magic Mixies Magic Genie Lamp',
        year: 'Launched 2023',
        color: '#FFB347',
        awards: [],
        blurb: 'The third major reveal vessel in the franchise — applying the core Magic Mixies mechanic to a genie lamp format. Kids follow spell steps on the lamp, which fills with mist to reveal a hidden Mixie inside. Released in multiple colourways including Pink, Blue, and a Rainbow exclusive edition, with multiple series waves confirming sustained retail performance. Launched alongside Pixlings as part of a deliberate expansion of the franchise in 2023 — showing that the reveal mechanic was format-agnostic and still capable of generating excitement three years into the brand\'s life.',
        imageFolder: 'lamp',
      },
      {
        id: '06',
        name: 'Magic Mixies Pixie Supremes',
        year: 'Launched 2024',
        color: '#FF69B4',
        awards: ['TOTY Finalist — Doll of the Year, 2025'],
        blurb: 'The premium flagship of the franchise. A 10.5-inch articulated fashion doll released from a Magic Mirror — kids mix a potion, press a crystal that flips the mirror, and the doll is freed in a burst of confetti. The Magic Mirror has 50+ lights and sounds and a Fortune Mode after-play function; the doll has nine points of articulation and five fashion accessories. The TVC produced for Pixie Supremes won a Muse Award in the Video/Children category. Nominated for TOTY Doll of the Year in 2025. The brief was to take Magic Mixies upmarket — a premium play experience that extended the franchise into new age groups and higher price points without losing the theatrical reveal at its heart.',
        imageFolder: 'pixie-supremes',
        reverseLayout: true,
      },
    ],
  },

  {
    id: '02',
    slug: 'little-live-pets',
    title: 'Little Live Pets',
    category: 'Global Multi Sub-Brand Identity Systems',
    client: 'Moose Toys',
    role: 'Art Director, Global Marketing',
    hue: '160',
    stat: 'DreamToys UK two years running',
    awards: [
      {
        title: 'DreamToys Top Toy — UK, 2022 & 2023',
        blurb: 'Selected by the UK\'s Toy Retailers Association as a must-have Christmas toy two years in a row — Mama Surprise in 2022, My Puppy\'s Home in 2023. DreamToys placement is driven by campaign visibility, retail creative, and consumer demand; the art direction and campaign work were central to earning both spots.',
      },
      {
        title: 'NAPPA Gold Award — Mouse House, 2015',
        blurb: 'National Parenting Product Award, USA. Judged by parents evaluating real-world play value — retail creative, brand presentation, and campaign materials all form part of the evaluation. Gold recognised Mouse House as one of the standout products of the year.',
      },
      {
        title: 'Toys"R"Us Fabulous 15 · Toy Insider Hot 20 · Walmart Chosen By Kids — 2015',
        blurb: 'Clever Keet appeared on three of the most competitive retailer and editorial hot toy lists in the US in a single holiday season. Retail presence, TV creative, and in-store creative were the primary drivers of that multi-list recognition.',
      },
      {
        title: 'Toys"R"Us · Walmart · Target Holiday Hot Lists — 2016',
        blurb: 'Snuggles My Dream Puppy was named to every major US retailer\'s holiday list simultaneously — a rare clean sweep achieved through coordinated campaign creative across TV, digital, in-store environments, and retail creative.',
      },
      {
        title: 'Toys"R"Us Partner of the Year — Moose Toys, 2015',
        blurb: 'Awarded during the period when Little Live Pets was Moose Toys\' lead retail driver. The award recognised the strength of the creative and marketing relationship — the quality of in-store execution, co-op creative, and campaign support was a central part of that partnership.',
      },
    ],
    heroImage: '/projects/little-live-pets/hero.webp',
    heroVideo: '/projects/little-live-pets/llp-TVC.mp4',
    images: [],
    overview:
      'A decade of creative leadership on one of Moose Toys\' most enduring brands. Little Live Pets built its reputation on a single idea — that a toy could feel genuinely alive — and the job was to protect and scale that idea across eleven distinct sub-brands and 50+ global markets.',
    brief:
      'Little Live Pets had already established itself as a category leader in interactive toys — the brief was to maintain that position across an expanding product family without diluting the core emotional connection the brand had built with kids and parents.\n\nEvery campaign extension needed to feel fresh while honouring what made the brand work: the idea that these toys genuinely feel alive.',
    direction:
      'Lead with the emotional truth. Little Live Pets\' entire equity lives in a single moment — when a child first holds one and it reacts. The creative direction centred that interaction at every touchpoint.\n\nWarm, joyful, and child-centred without being patronising. The visual language balanced product functionality with genuine warmth — showing kids in real moments of connection, not staged demonstrations.',
    execution:
      'Full brand system maintained and extended across multiple product lines spanning a decade: Mama Surprise, Scruff-a-Luvs, Rolly Chick, Snuggles My Dream Puppy, Clever Keet, Mouse House, and seasonal extensions. TV and digital campaign assets art-directed globally, with retail POS and trade show creative rolled out across 50+ markets.\n\nStyle guide managed to ensure visual consistency across a large and continuously growing product portfolio, from initial concept through global retail delivery.',
    results:
      '#2 US Special Feature Plush by dollar velocity (NPD, 2022). DreamToys placement two years running. On every major US retailer holiday hot list — Walmart, Target, Toys“R”Us. #3 Youth Electronics in Australia (Circana).',
    tags: ['Brand System', 'Campaign', 'TV', 'Retail POS', 'Global', 'Style Guide', 'Retail POS', 'DreamToys'],
    subBrands: [
      {
        id: '1',
        name: 'Mama Surprise Families',
        segment: 'Reveal · Extension',
        color: '#FF6B9D',
        year: '2022 — 2024',
        awards: [],
        blurb: 'A direct extension of the Mama Surprise mechanic — applying the reveal and nurture play pattern to a wider family format with multiple babies and collectible characters. The creative challenge was maintaining the warmth and excitement of the hero line while expanding the range for a broader age group and price tier.',
        imageFolder: 'mama-surprise-families',
        reverseLayout: true,
      },
      {
        id: '10',
        name: 'Hug N\' Hang Zoogooz',
        segment: 'Interactive · Stretch Play',
        color: '#9CCC65',
        year: '2020 — 2022',
        awards: [],
        blurb: 'Stretchy, squeezable animal characters that respond to touch and hang from surfaces. Zoogooz sat at the intersection of sensory play and interactivity — the creative had to communicate a tactile quality that product photography alone couldn\'t fully capture. Retail creative leaned on in-store demonstration and unboxing moments to drive trial.',
        imageFolder: 'hug-n-hang-zoogooz',
      },
      {
        id: '2',
        name: 'My Baby Monkey',
        segment: 'Nurture · Emotional Play',
        color: '#FF8A50',
        year: '2022 — 2024',
        awards: [],
        blurb: 'A nurture-play extension within the My Baby Animal family. My Baby Monkey leaned into the emotional connection kids form with primates — responsive to touch, with sounds and movements designed to feel genuinely alive. Creative direction balanced product functionality with the intimate, parental moment that defines the My Baby Animal segment.',
        imageFolder: 'my-baby-monkey',
        reverseLayout: true,
      },
      {
        id: '3',
        name: 'My Baby Panda',
        segment: 'Nurture · Emotional Play',
        color: '#78909C',
        year: '2021 — 2023',
        awards: [],
        blurb: 'A fan-favourite species extension within the My Baby Animal range. Pandas carry inherent emotional weight — the creative leaned into that, letting the product\'s reactions and sounds do the storytelling. Art direction across retail POS and digital emphasised texture and tactility to communicate the softness of the plush at every touchpoint.',
        imageFolder: 'my-baby-panda',
      },
      {
        id: '4',
        name: 'My Really Real Puppy',
        segment: 'High-Realism · Interactive',
        color: '#FF7043',
        year: '2019 — 2024',
        awards: [],
        blurb: 'The hero SKU of the My Really Real range — a dog with high-fidelity fur, lifelike sounds, and multi-stage interactive responses. Creative required a grounded, tactile approach distinct from the rest of the LLP range. Leaned on cinematic product photography and close-up detail work to communicate what made these toys feel premium on shelf.',
        imageFolder: 'my-really-real',
        reverseLayout: true,
      },
      {
        id: '5',
        name: 'My Really Real Bird & Cage',
        segment: 'High-Realism · Interactive',
        color: '#66BB6A',
        year: '2021 — 2023',
        awards: [],
        blurb: 'A species extension in the My Really Real family — a realistic bird with responsive movement and sound, sold with a playable cage habitat. The creative challenge was making a bird feel as emotionally compelling as a puppy. Campaign direction leaned on the bird\'s lifelike chirping and head-tilting reactions, positioning it as genuinely alive rather than a feature demonstration.',
        imageFolder: 'my-really-real-bird',
      },
      {
        id: '6',
        name: 'My Puppy\'s Home',
        segment: 'Habitat · Interactive Play',
        color: '#00BCD4',
        year: '2023 — 2024',
        awards: ['DreamToys Top Toy — UK, 2023'],
        blurb: 'The product that earned Little Live Pets its second consecutive DreamToys placement in the UK. My Puppy\'s Home combined the brand\'s interactive pet mechanic with a fully playable home environment. Creative direction balanced product demonstration with emotional storytelling — a puppy that genuinely feels like it belongs in its home.',
        imageFolder: 'my-pets-home',
        reverseLayout: true,
      },
      {
        id: '8',
        name: 'Needees',
        segment: 'Collectibles · Nurture',
        color: '#26C6DA',
        year: '2020 — 2022',
        awards: [],
        blurb: 'Collectible blind-bag characters, each with a specific emotional need kids identify and respond to. The creative challenge was building an identity system that gave individual characters personality while functioning as a cohesive collectible range. Retail creative, brand hierarchy, and the reveal mechanic were all part of the brand system.',
        imageFolder: 'needees',
      },
      {
        id: '7',
        name: 'My Walking Pet',
        segment: 'Walking · Interactive',
        color: '#7B5EA7',
        year: '2013 — ongoing',
        awards: [],
        blurb: 'The founding segment of Little Live Pets — animals that walk, chirp, and react to touch. Keeping this line visually fresh across a decade of successive waves, new animal species, and changing retail environments was the core creative challenge. The single emotional truth never changed: these toys feel genuinely alive.',
        imageFolder: 'my-walking-pet',
        reverseLayout: true,
      },
      {
        id: '9',
        name: 'Cozy Dozy',
        segment: 'Plush · Comfort Play',
        color: '#EC407A',
        year: '2022 — 2024',
        awards: [],
        blurb: 'The softest expression of the LLP brand — plush animals that snuggle, snore, and respond to being held. Cozy Dozy extended the brand into bedtime and comfort play without losing the interactive equity that defines LLP. A warmer, softer visual language distinguished it from the brighter segments while keeping it clearly part of the family.',
        imageFolder: 'cozy-dozy',
        compact: true,
      },
      {
        id: '11',
        name: 'Mama Surprise',
        segment: 'Reveal · Nurture Play',
        color: '#E91E8C',
        year: '2021 — 2024',
        awards: ['DreamToys Top Toy — UK, 2022'],
        blurb: 'The franchise flagship and its biggest commercial hit. Mama gives birth to a litter of babies inside a surprise reveal — kids feed, nurture, and care for Mama throughout the experience. Launched across rabbit, cat, dog, guinea pig, and unicorn species. Campaign art direction spanned TV, digital, retail POS across 50+ markets. Named a driver of Plush becoming the fastest-growing toy supercategory in the US in 2022.',
        imageFolder: 'mama-surprise',
        compact: true,
      },
    ],
  },

  {
    id: '03',
    slug: 'airnorth-airlines',
    title: 'Airnorth Airlines',
    category: 'Brand Identity · 360 Content System · Television Commercials',
    client: 'Airnorth',
    role: 'Art Director · Brand Lead',
    hue: '210',
    stat: 'Design System built to scale',
    heroImage: '/projects/Airnorth/hero-event.webp',
    heroVideo: '/projects/Airnorth/airnorthTVC.mp4',
    sections: [
      {
        label: 'TV Commercial',
        videos: [
          { src: '/projects/Airnorth/tVC.mp4', w: 1920, h: 1080 },
        ],
      },
      {
        label: 'Billboard',
        images: [
          { src: '/projects/Airnorth/Billboard-01.webp', aspect: '16:9', label: 'Billboard' },
        ],
      },
      {
        label: 'Airport Branding',
        images: [
          { src: '/projects/Airnorth/airport-01.webp', aspect: '16:9', label: 'Airport Brand Environment · Darwin' },
          { src: '/projects/Airnorth/airport-03.webp', aspect: '16:9', label: 'Check-in Environment · Terminal Signage' },
        ],
      },
      {
        label: 'Digital Billboards',
        images: [
          { src: '/projects/Airnorth/digital-01.webp', aspect: '1:1', label: 'Digital Billboard 01' },
          { src: '/projects/Airnorth/digital-02.webp', aspect: '1:1', label: 'Digital Billboard 02' },
        ],
      },
      {
        label: 'Print and Storefront',
        mixed: [
          { src: '/projects/Airnorth/ShopFront.webp',  w: 1080, h: 1080 },
          { src: '/projects/Airnorth/Newspaper.webp',  w: 1920, h: 1080 },
        ],
      },
      {
        label: 'DL Flyers',
        mixed: [
          { src: '/projects/Airnorth/DL-01.webp', w: 1920, h: 1080 },
          { src: '/projects/Airnorth/DL-02.webp', w: 1080, h: 1080 },
        ],
      },
      {
        label: 'Motion Posters',
        videos: [
          { src: '/projects/Airnorth/video1.mp4', w: 1080, h: 1080, noSound: true },
          { src: '/projects/Airnorth/video2.mp4', w: 1920, h: 1080, noSound: true },
        ],
      },
      {
        label: 'Social',
        mixed: [
          { src: '/projects/Airnorth/Social1.webp', w: 1080, h: 1080 },
          { src: '/projects/Airnorth/Social2.webp', w: 1080, h: 1080 },
          { src: '/projects/Airnorth/Social3.webp', w: 1080, h: 1080 },
        ],
      },
    ],
    overview:
      'Airnorth connects remote Northern Australia and the Pacific. Darwin, Cairns, Townsville, the Kimberley, Dili. I built the brand from the ground up: identity system, tagline, television commercial, and a complete suite of campaign and environmental touchpoints across every market the airline serves.\n\nThe brief was deceptively clean. Make a small regional carrier feel essential, without pretending to be something it was not.',
    brief:
      'Regional airlines don\'t win on scale. They win on belonging.\n\nAirnorth\'s edge was intimacy. It knew its routes, its communities, and its passengers in ways no major carrier ever would. The job was to translate that into a brand system with enough confidence to hold its own on a Darwin airport billboard, a Cairns shopping centre hoarding, and a prime-time television commercial, all at once.\n\nThe work needed to perform across formats most airline brands never encounter. It had to carry weight in remote terminal departure halls and Pacific island markets just as convincingly as it did on a national broadcast.',
    direction:
      '"Together We Fly" arrived early and shaped everything that followed. The line worked on two levels simultaneously: the straightforward act of travelling together, and the deeper idea that Airnorth exists to connect the people and places of the north.\n\nVisually, I kept the direction warm, grounded, and unhurried. Northern Australia\'s landscapes, festivals, and communities as the primary subject. Not aircraft. Not terminals. The people at either end of each route were always the hero.\n\nPhotography over abstraction. Warmth over precision. A brand system built to feel familiar to the communities it serves, not impressive to the industry observing it.',
    execution:
      'I art directed the complete brand system: logo, colour, typography, and guidelines. Then applied it across the full scope of formats the airline operates in. Television commercial production, large-format outdoor billboards, airport check-in standees, pull-up banners, shopping centre portrait media, digital campaigns, and print collateral.\n\nThe system was built to perform in conditions most airline brands never face. Remote terminal departure halls, Indigenous community hoardings, Pacific island markets. It held across all of them without compromise or adaptation.\n\nEvery format was designed natively. Nothing was cropped from something else. That discipline is what makes a brand system actually scale.',
    results:
      'Airnorth went from a carrier with no coherent brand voice to one with a clear identity, a tagline that genuinely reflected who they were, and a visual system built to grow with the business.\n\nFrom a 3 x 6m airport billboard to a baggage-limit standee, the creative held without breaking. That is the standard in regional airline branding, and it is the hardest one to meet.',
    tagline: 'Together We Fly',
    logo: '/projects/Airnorth/Logo.webp',
    tags: ['Brand Identity', 'TVC', 'Art Direction', 'Outdoor', 'Airport Environments', 'Print', 'Regional'],
  },

  {
    id: '04',
    slug: 'vichealth',
    title: 'VicHealth',
    category: 'Government · Integrated Campaign · OOH',
    client: 'VicHealth',
    role: 'Art Director · Creative Lead',
    hue: '200',
    stat: 'Violence Against Women',
    heroImage: '/projects/vichealth/hero.webp',
    heroVideo: '/projects/vichealth/vicHealth%20Campaign.mp4',
    videoStartTime: 1,
    pageHeroImage: '/projects/vichealth/Hero.webp',
    images: [],
    overview:
      'A government-backed primary prevention campaign tackling family violence across Victoria. The brief required reaching multicultural communities with a message serious enough to matter and considered enough not to cause harm. I led the creative direction across video, digital, OOH, and community language adaptations.',
    brief:
      'The hardest briefs are the ones where the wrong creative decision doesn\'t just miss the mark — it actively hurts people. This campaign had to shift community attitudes around family violence across Victoria\'s diverse multicultural population, reach survivors without retraumatising them, engage bystanders without alienating them, and land consistently across every channel and community language.\n\nShock tactics were off the table. Research in this space is clear: graphic imagery of violence desensitises audiences and overpowers the message. The brief demanded something more considered.',
    direction:
      'The core creative decision was to show what safety and respect look like, not what violence looks like. That single principle shaped every choice that followed.\n\nColour was deliberate. We moved away from high-energy reds and stark clinical whites and built the palette around warm, grounded tones — colours that feel like community, not crisis. The goal was for someone to see this on a bus shelter or a community centre wall and feel approached, not confronted.\n\nIllustration did work that photography couldn\'t. Photography of real people in scenarios involving harassment or abuse risks exploitation and can function as a trigger for survivors. Illustration creates the right distance — it lets you show the scenario, model the behaviour, and represent diverse communities without any of those risks. Stylistically, we kept it human and warm rather than graphic or corporate.\n\nThe bystander was the audience, not the victim. Positioning the community as the protagonist — people who can see something and step in, people who model respect in everyday situations — reframes the campaign from warning to invitation.',
    execution:
      'Creative direction across the full campaign: video production, digital assets, print, and OOH. The video was built around the empowerment framing — community members in real situations, modelling what intervention and respect look like rather than depicting harm.\n\nIllustration development for toolkit assets, designed to be flexible enough for partner organisations to adapt and localise. Community language adaptations produced for multicultural audiences including Arabic, Simplified Chinese, Vietnamese, and Punjabi communities, each reviewed for cultural appropriateness rather than translated wholesale.\n\nAll creative was tested against a key check before release: does this material cause harm to someone who is currently experiencing violence? Every executional decision ran through that filter.',
    results:
      'The campaign reached communities across metropolitan and regional Victoria through broadcast, programmatic digital, and OOH. Multicultural adaptations extended reach across communities where mainstream campaigns rarely land with relevance. The creative held its integrity across every format — from a 30-second TVC to a community centre flyer — because the visual system was built to scale, not just to launch.',
    tags: ['Integrated Campaign', 'Government', 'Social Impact', 'OOH', 'Community'],
    storyboardSection2: {
      video: '/projects/vichealth/vicHealthSurvey.mp4',
      trioImages: [
        '/projects/vichealth/02a.webp',
        '/projects/vichealth/02b.webp',
        '/projects/vichealth/02c.webp',
      ],
      images: [
        '/projects/vichealth/02/17859954-6925-4de9-bae8-981d097ea95e_rw_1920.webp',
        '/projects/vichealth/02/190bc12d-6981-4c9a-b643-9535d732da4c_rw_1920.webp',
        '/projects/vichealth/02/26638e14-57cd-41fb-a6e7-ea9e031355df_rw_1920.webp',
        '/projects/vichealth/02/2cf1375f-c10b-428e-95fc-4f39cf06f115_rw_1920.webp',
        '/projects/vichealth/02/2fbab714-93ac-4884-90d9-8b34e5a1d2c8_rw_1920.webp',
        '/projects/vichealth/02/3ed7d69a-0ce0-450b-b564-26f95c6c19b9_rw_1920.webp',
        '/projects/vichealth/02/3f43460e-7a90-4b5c-b68e-5a145b061b8b_rw_1920.webp',
        '/projects/vichealth/02/441250d6-24c1-47cb-a97d-fb065a3b2d29_rw_1920.webp',
        '/projects/vichealth/02/476f410a-6f1a-40d6-bb02-3c891f7aabf5_rw_1920.webp',
        '/projects/vichealth/02/485d00c6-c5d9-4f74-9295-d8d1cf2fc58b_rw_1920.webp',
        '/projects/vichealth/02/50401d64-2414-4d5b-ae19-6541a39ee1b3_rw_1920.webp',
        '/projects/vichealth/02/536c8ca4-8123-4e4f-876c-c624832fb34e_rw_1920.webp',
        '/projects/vichealth/02/5efa52d3-148f-419a-aa99-008fdc5c3edf_rw_1920.webp',
        '/projects/vichealth/02/64191697-bded-4433-b3c2-3ef45b55782b_rw_1920.webp',
        '/projects/vichealth/02/6a29c574-2ffd-4d7e-8343-deb3b9309f5f_rw_1920.webp',
        '/projects/vichealth/02/74caa1d8-50ff-4db4-b9b9-2b27fa8bb40b_rw_1920.webp',
        '/projects/vichealth/02/80a796cc-bc3a-472a-85ce-9c37118dc8ed_rw_1920.webp',
        '/projects/vichealth/02/8315b473-b259-45e4-bba2-a2fd1b8450eb_rw_1920.webp',
        '/projects/vichealth/02/86272bd4-2b6a-4e69-8e37-65eb08f29264_rw_1920.webp',
        '/projects/vichealth/02/87d584a1-d6b8-4620-8c55-5f25ff530d89_rw_1920.webp',
        '/projects/vichealth/02/a8224152-a172-48cf-9e85-b6bbc6a05594_rw_1920.webp',
        '/projects/vichealth/02/a8fdc3ca-b694-4bb6-9595-9380ccf722d6_rw_1920.webp',
        '/projects/vichealth/02/b2b4d9bf-adea-4eaa-a7b7-63ad1a87523f_rw_1920.webp',
        '/projects/vichealth/02/bbb19235-a64c-40c1-8c13-d388cd938a52_rw_1920.webp',
        '/projects/vichealth/02/c9eaff4d-0b4a-439e-98c0-37dc64474657_rw_1920.webp',
        '/projects/vichealth/02/cb4a97c5-1337-4a1f-a385-0f7f79633108_rw_1920.webp',
        '/projects/vichealth/02/cf172884-bb30-49e2-a423-ebd99b3a510b_rw_1920.webp',
        '/projects/vichealth/02/d37e3fd8-3da0-42e3-a115-d7250bf17011_rw_1920.webp',
        '/projects/vichealth/02/d919bf29-81a7-47b0-9294-edb4d4dd5295_rw_1920.webp',
        '/projects/vichealth/02/d9cff042-77d9-4a4e-a47e-4819ff8989f1_rw_1920.webp',
        '/projects/vichealth/02/da484d90-f61a-4703-90a3-3a513dcd6300_rw_1920.webp',
        '/projects/vichealth/02/de40ce26-f376-47d4-b346-aa606f10d037_rw_1920.webp',
        '/projects/vichealth/02/dfa7f69a-2d27-488a-8117-47fa886a45c5_rw_1920.webp',
        '/projects/vichealth/02/e19de9d0-fa5b-4336-938e-ea1f61db9342_rw_1920.webp',
        '/projects/vichealth/02/e39376ee-1fe4-41fa-b3cf-9b5afbb1810d_rw_1920.webp',
        '/projects/vichealth/02/e4cecf60-ff96-442a-9e52-12c179583e11_rw_1920.webp',
        '/projects/vichealth/02/f10f6723-b574-4b22-8b4d-1131c9339369_rw_1920.webp',
        '/projects/vichealth/02/fa4ddfa1-f437-43ef-a0c7-a8260d73aad9_rw_1920.webp',
      ],
    },
    storyboardSection: {
      video: '/projects/vichealth/vicHealth%20Campaign.mp4',
      trioImages: [
        '/projects/vichealth/01a.webp',
        '/projects/vichealth/01b.webp',
        '/projects/vichealth/01c.webp',
      ],
      images: [
        '/projects/vichealth/01/151194ba-2b44-43ed-b49c-6118976ff153_rw_1920.webp',
        '/projects/vichealth/01/177deb06-63c5-442f-85d4-15a45011693b_rw_1920.webp',
        '/projects/vichealth/01/259bbd96-3285-4bcb-a027-ebb15a605404_rw_1920.webp',
        '/projects/vichealth/01/376b4714-fd54-4d79-b9c9-d3eba3329168_rw_1920.webp',
        '/projects/vichealth/01/71cbc861-c598-4787-bf1b-974cf2bf96b5_rw_1920.webp',
        '/projects/vichealth/01/827210db-d7b5-4448-9808-a58cc1ff4d6b_rw_1920.webp',
        '/projects/vichealth/01/93b010a7-c908-438a-9de3-7690034895ae_rw_1920.webp',
        '/projects/vichealth/01/aab2af2a-4601-4fe1-bfd4-8ddb0e8dfefb_rw_1920.webp',
        '/projects/vichealth/01/ad3320aa-a189-4bb2-8b24-b05ddc8e992d_rw_1920.webp',
        '/projects/vichealth/01/ad35c2bd-f2d9-4d69-be04-4e20ca56b882_rw_1920.webp',
        '/projects/vichealth/01/af8ac386-13f1-4298-90ce-aee1268c834a_rw_1920.webp',
        '/projects/vichealth/01/d6a03017-f145-4468-8107-e31b7bd81e96_rw_1920.webp',
        '/projects/vichealth/01/d9d71ec0-50cc-4f30-9be9-772f829ae5e1_rw_1920.webp',
        '/projects/vichealth/01/dcd8c3c5-d871-462a-a6bf-a86d20b9786c_rw_1920.webp',
        '/projects/vichealth/01/dd727aa2-df4c-447f-8ffd-3ed1268dacdb_rw_1920.webp',
        '/projects/vichealth/01/ed2ced67-2eef-4644-974e-d1f2bf43445f_rw_1920.webp',
        '/projects/vichealth/01/f4b5c388-a48f-461e-b306-7107127a03a6_rw_1920.webp',
        '/projects/vichealth/01/f5e51a7e-1a61-4a6c-b117-2e2019610587_rw_1920.webp',
        '/projects/vichealth/01/fed76608-5f1e-4783-bc96-32907c205b98_rw_1920.webp',
        '/projects/vichealth/01/ff860ad7-1c19-43cc-8d70-8185bae0836d_rw_1920.webp',
      ],
    },
  },

  {
    id: '05',
    slug: 'gui-gui',
    title: 'Gui Gui',
    category: 'OOH · Campaign · Social',
    client: 'Moose Toys',
    role: 'Art Director, Global Marketing',
    hue: '38',
    stat: 'TOTY Nominated · Global Sell-Out',
    heroImage: '/projects/gui-gui/hero.webp',
    heroVideo: '/projects/gui-gui/gui%20Gui%20TVC.mp4',
    galleryVideo: '/projects/gui-gui/guiguits.mp4',
    galleryVideoLabel: 'Times Square — Campaign',
    galleryLabel: '360 Marketing Campaign',
    images: [
      { src: '/projects/gui-gui/01.webp', label: 'Times Square Placement', aspect: '16:9' },
      { src: '/projects/gui-gui/02.webp', label: 'Campaign Visual', aspect: '16:9' },
      { src: '/projects/gui-gui/03.webp', label: 'Social Content', aspect: '1:1' },
      { src: '/projects/gui-gui/04.webp', label: 'OOH Creative', aspect: '16:9' },
      { src: '/projects/gui-gui/05.webp', label: 'Digital Campaign', aspect: '16:9' },
      { src: '/projects/gui-gui/06.webp', label: 'Organic Reach', aspect: '1:1' },
    ],
    overview:
      'Gui Gui didn\'t launch into an existing category. It created one. A beauty-inspired slime brand sitting at the intersection of the $33.9 billion cosmetics industry and a craft format that had already clocked 78 billion views on TikTok — this was slime repositioned as a self-expression product for girls, not a kids\' craft kit.\n\nThe campaign built to match it was 360 in the truest sense: retail, digital, social, influencer, PR, and a New York Times Square placement that became the campaign\'s defining moment. The creative earned its own audience — no paid amplification required.',
    brief:
      'The product brief was as much a category brief as a brand one. Gui Gui needed to occupy a space that didn\'t exist yet — beauty play for girls who were ageing out of traditional craft but not yet ready for real cosmetics. That meant the creative had to feel premium, aspirational, and sensory in a way toy advertising rarely does.\n\nThe campaign brief followed the same logic: don\'t look like a toy campaign. Look like a beauty launch. The Times Square placement wasn\'t a media buy bolted on at the end — it was conceived from the start as the campaign\'s centrepiece, the moment designed to travel.',
    direction:
      'The creative direction pulled from beauty industry codes, not toy marketing conventions. High-gloss finishes. Saturated, cosmetic-grade colour. Tactile, ASMR-adjacent imagery that made you want to reach through the screen. The visual language was closer to a Glossier launch than a Walmart toy aisle.\n\nFor the Times Square execution, every decision was filtered through a single question: does this look extraordinary on someone\'s phone, filmed from street level in New York City? Scale, luminosity, and motion were all calibrated for that moment — not for the screen it was playing on, but for the screens it would be replayed on.',
    execution:
      'The campaign ran across every relevant touchpoint. Digital and social assets were built in parallel with TVC, each format designed to pull its own weight rather than repurpose the same creative. Influencer content was aligned with ASMR and beauty trends on YouTube and TikTok, tapping into the organic language of the slime community rather than broadcasting over it.\n\nThe Times Square placement was art directed specifically for the environment — timed for peak foot traffic, built to stop people mid-stride. Social capture strategy ran alongside the OOH window to turn passive viewers into active distributors. The placement became the earned media engine, not the endpoint.\n\nRetail creative extended the beauty positioning into the physical shelf — packaging and POS designed to stand apart in the craft aisle and signal something different to the girl picking it up.',
    results:
      'Gui Gui sold out at major retailers globally within weeks of launch, earned a TOTY nomination for Creative Toy of the Year, and went on to partner with influencers reaching a combined 35M+ followers. The Times Square placement became the campaign\'s most-shared organic moment — proof that the creative direction was right. Slime as beauty product landed. The category exists now because this campaign made it real.',
    tags: ['360 Campaign', 'OOH', 'Social Content', 'Art Direction', 'Beauty x Slime', 'New Category', 'Global'],
  },

  {
    id: '06',
    slug: 'bluey',
    title: 'Bluey',
    category: 'Licensed Brand · Global Campaign',
    client: 'Moose Toys',
    role: 'Art Director, Global Marketing',
    hue: '200',
    stat: 'TOTY License of the Year 2025',
    awards: [
      {
        title: 'TOTY License of the Year — Bluey, 2025',
        blurb: 'Awarded by The Toy Foundation at the annual Toy of the Year gala — recognising the total commercial performance of the Bluey toy range, including retail presentation and campaign creative across global markets.',
      },
      {
        title: 'UK Toy Industry Awards — Preschool Toy of the Year, 2025',
        blurb: 'Won at London Toy Fair — one of the most competitive trade award ceremonies in the UK market. Trade show display and retail sell-in creative were central to earning the recognition.',
      },
      {
        title: 'DreamToys Top 15 — Bluey 3-in-1 Transforming Plane, 2024',
        blurb: 'Selected by the UK\'s Toy Retailers Association as one of the most anticipated Christmas toys of the year. DreamToys placement is driven by campaign visibility, retail creative, and consumer demand — all within the marketing art direction scope.',
      },
      {
        title: 'Best Licensed Toys & Games Range — British Licensing Awards, 2022',
        blurb: 'Industry recognition for the quality and commercial performance of the Bluey licensed toy range across the UK market — reflecting the strength of brand presentation and retail creative.',
      },
      {
        title: 'ATA Licensed Toy of the Year — 2021 & 2022',
        blurb: 'Won at the Australian Toy Association Awards two consecutive years. The ATA awards judge brand presentation, retail execution, and marketing quality alongside product — directly tied to the art direction work on POS and trade show creative.',
      },
      {
        title: '#1 Preschool Licence — UK & Australia · #2 Preschool Character — USA (Circana, 2023)',
        blurb: 'Market-leading positions across three major territories, driven by consistent campaign creative and retail execution. In-store environments, and campaign assets art directed across 50+ markets.',
      },
    ],
    heroImage: '/projects/bluey/Thumbnail.webp',
    heroVideo: '/projects/bluey/hero.mp4',
    images: [],
    galleryComingSoon: true,
    overview:
      'Art direction and campaign creative for Moose Toys\' Bluey licensed range — one of the most successful children\'s toy licences of the decade. Bluey\'s transition from beloved Australian TV show to global toy phenomenon required creative that honoured the property\'s warmth and wit while performing across 50+ retail markets.',
    brief:
      'Bluey is one of the few licensed properties where the brand equity is so strong that the creative challenge is restraint — not innovation. The brief was to build a global campaign and retail system that let the property\'s characters do the work, without over-producing the magic that makes Bluey feel genuinely special to kids and parents alike.',
    direction:
      'Stay true to the show\'s visual handwriting. Bluey\'s distinctive illustration style — warm, expressive, and deceptively simple — was the foundation for every creative decision. The campaign extended that world into toy photography, retail environments, and digital creative without losing the hand-drawn warmth that defines the property.\n\nWhere most toy campaigns push product first, Bluey demanded character first — the toys exist inside the world, not the other way around.',
    execution:
      'Full campaign art direction across TV, digital, retail POS for global launch. Style guide developed and maintained to ensure brand consistency across Moose Toys\' international markets. Trade show creative built for New York Toy Fair and key retail events. Digital and social assets produced for 50+ market rollout.',
    results:
      'Bluey became one of the strongest licensed toy ranges in Moose Toys\' portfolio, winning Best Licensed Toys & Games Range at the British Licensing Awards in 2022. The property\'s cultural momentum — driven by the show\'s global streaming reach — combined with strong in-store creative execution to make it a consistent retail performer across key markets.',
    tags: ['Licensed Brand', 'Campaign', 'Retail POS', 'Retail POS', 'Style Guide', 'Global', 'Trade Show'],
  },

  {
    id: '08',
    slug: 'pikmi-pops',
    comingSoon: true,
    title: 'Pikmi Pops',
    category: 'Visual Identity · Marketing Assets',
    client: 'Moose Toys',
    role: 'Art Director, Global Marketing',
    hue: '300',
    stat: 'Candy-bright identity. Premium shelf position. 50+ markets.',
    heroImage: '/projects/pikmi-pops/hero.webp',
    heroVideo: '/projects/pikmi-pops/pikmi.mp4',
    images: [],
    galleryComingSoon: true,
    overview:
      'Visual identity and marketing asset collection for Pikmi Pops — scented collectible plush toys packaged in oversized lollipops. The challenge was to make candy-bright feel elevated.',
    brief:
      'Pikmi Pops had an instantly compelling product premise: a giant lollipop packaging that contained a scented plush surprise. The brief was to build a visual identity that leaned into the candy-sweet concept without tipping into cheap or garish — the goal was a brand that felt joyful and desirable, not just loud.',
    direction:
      'Sweet with substance. The creative direction balanced the product\'s inherently playful, sugary aesthetic with a visual language refined enough to hold premium shelf positioning.\n\nTexture, shine, and typographic playfulness were the pillars — every element had a slightly confectionary quality, but the overall composition always maintained clarity and intentionality.',
    execution:
      'Full visual identity: logo, colour system, typography, and retail POS direction. Marketing asset collection spanning digital, social, retail POS, and trade show.\n\nStyle guide developed for global rollout across 50+ markets, with clear direction for photography, colour usage, and layout — allowing regional teams to maintain the brand\'s distinct personality without direct oversight.',
    results:
      'Pikmi Pops launched with a distinctive identity that drove strong on-shelf presence and built a loyal collector community globally. The brand\'s visual personality was central to its social media traction — the candy aesthetic translated exceptionally well to Instagram and YouTube unboxing content.',
    tags: ['Visual Identity', 'Retail POS', 'Marketing Assets', 'Social', 'Global', 'Style Guide'],
  },

  {
    id: '09',
    slug: 'real-littles',
    hidden: true,
    comingSoon: true,
    title: 'Shopkins Real Littles',
    category: 'Style Guide · Marketing System',
    client: 'Moose Toys',
    role: 'Art Director, Global Marketing',
    hue: '210',
    stat: 'TOTY Finalist · Sold out at US retail within weeks of launch',
    awards: [
      {
        title: 'TOTY Finalist — Collectible of the Year, 2020',
        blurb: 'Named a Toy of the Year finalist by The Toy Association in the Collectible category for the inaugural launch series. Retail creative and brand presentation are core evaluation criteria — the unboxing experience and shelf impact were central to the nomination.',
      },
      {
        title: 'Toy Insider Holiday Gift Guide — 2019',
        blurb: 'Named a top holiday pick by The Toy Insider, one of the most influential US toy editorial platforms. Editorial picks of this kind are driven by product presentation, retail creative and brand storytelling — all within the art direction scope.',
      },
      {
        title: 'National Parenting Product Awards — 2019',
        blurb: 'Recognised by the NAPPA Awards, judged by parents evaluating real-world play value. Retail creative and brand presentation are key factors in how parents encounter and evaluate products — a direct outcome of the creative work.',
      },
      {
        title: 'Sold out at US retail within weeks of launch — August 2019',
        blurb: 'Series 1 sold through at retail faster than Moose Toys anticipated, directly triggering Series 2 development. Documented in trade press. Shelf impact, shelf presence, and campaign creative were the consumer-facing drivers of that sell-through velocity.',
      },
    ],
    heroImage: '/projects/real-littles/hero.webp',
    heroVideo: null,
    images: [],
    galleryComingSoon: true,
    overview:
      'A global brand system for Real Littles — miniature replicas of real shopping bags and accessories at collectible scale. The work centred on making tiny things feel genuinely premium.',
    brief:
      'Real Littles was built on an irresistible proposition: exact replicas of iconic brand bags, made tiny enough to hold in your hand. The creative challenge was to communicate quality and authenticity — to make a toy feel like a luxury collectible. The brief was to build a style guide and marketing asset system that held that bar globally across every touchpoint and market.',
    direction:
      'The creative direction anchored on two things: authenticity and aspiration. These weren\'t cheap imitations — they were precise miniatures. Every piece of communication needed to make the viewer want to pick them up.\n\nPhotography art direction mirrored fashion and luxury retail references rather than toy convention. Clean backgrounds, considered lighting, and tight shots that showed off the replica detail.',
    execution:
      'Comprehensive style guide developed covering typography, photography art direction, digital template system, social format library, and retail POS guidelines. Built for scale — the guide was designed to be handed to regional teams in 50+ markets and produce consistent output without requiring a brief for every asset.\n\nTrade show creative developed for New York Toy Fair and Nuremberg.',
    results:
      'Series 1 sold out at US retail within weeks of launch in August 2019 — documented in trade press and cited in Moose Toys\' own communications as the trigger for Series 2 development. The launch earned a TOTY Collectible of the Year nomination, Toy Insider Holiday Gift Guide placement, and National Parenting Product Awards recognition. The style guide and creative system built for launch became the foundation for Real Littles\' sustained growth across multiple season extensions and 50+ markets.',
    tags: ['Style Guide', 'Marketing System', 'Digital', 'Social', 'Retail POS', 'Global'],
  },

  {
    id: '07',
    slug: 'adverto',
    title: 'Adverto',
    category: 'Digital Signage · Product Marketing',
    client: 'Adverto',
    role: 'Creative Lead · AI Visualisation · Catalogue Design',
    hue: '40',
    heroImage: '/projects/adverto/adverto 6.webp',
    heroImagePosition: 'center 20%',
    images: [],
    sections: [
      {
        label: 'Pump-Mounted Range',
        rows: [
          {
            images: [
              { src: '/projects/adverto/adverto 1.webp', aspect: '16:9' },
            ],
          },
          {
            images: [
              { src: '/projects/adverto/adverto 2.webp', aspect: '1:1' },
              { src: '/projects/adverto/adverto 3.webp', aspect: '1:1' },
              { src: '/projects/adverto/adverto 4.webp', aspect: '1:1' },
            ],
          },
        ],
      },
      {
        label: 'Free Standing Range',
        rows: [
          {
            images: [
              { src: '/projects/adverto/adverto 5.webp', aspect: '16:9' },
            ],
          },
          {
            images: [
              { src: '/projects/adverto/adverto 6.webp', aspect: '1:1' },
              { src: '/projects/adverto/adverto 7.webp', aspect: '1:1' },
              { src: '/projects/adverto/adverto 8.webp', aspect: '1:1' },
            ],
          },
        ],
      },
      {
        label: 'Dual-Sided Range',
        rows: [
          {
            images: [
              { src: '/projects/adverto/adverto 9.webp', aspect: '16:9' },
            ],
          },
          {
            images: [
              { src: '/projects/adverto/adverto 10.webp', aspect: '1:1' },
              { src: '/projects/adverto/adverto 11.webp', aspect: '1:1' },
              { src: '/projects/adverto/adverto 12.webp', aspect: '1:1' },
            ],
          },
        ],
      },
      {
        label: 'Product Catalogue',
        rows: [
          {
            images: [
              { src: '/projects/adverto/catalogue1.webp', aspect: '16:9', contain: true },
            ],
          },
          {
            images: [
              { src: '/projects/adverto/catalogue2.webp', aspect: '1:1', contain: true },
              { src: '/projects/adverto/catalogue3.webp', aspect: '1:1', contain: true },
              { src: '/projects/adverto/catalogue4.webp', aspect: '1:1', contain: true },
            ],
          },
        ],
      },
    ],
    overview:
      'Adverto needed a professional product catalogue but had limited real-world photography of its digital screen products in market. I developed an AI-assisted visualisation workflow to create realistic product mockups across petrol forecourts, convenience stores, retail environments and outdoor digital signage applications.\n\nThe result was a scalable library of premium marketing visuals that reduced photoshoot dependency, accelerated go-to-market timelines and helped present the products in a more polished, commercial and sales-ready way.',
    brief:
      'The challenge was straightforward but the constraint was real: Adverto had market-ready digital screen products and needed professional marketing collateral — but no library of in-situ photography. Real-world photoshoots across petrol forecourts, convenience stores and retail environments are expensive, logistically complex and slow.\n\nThe brief was to find a smarter path — one that could produce catalogue-quality product visuals at speed, at scale, and without a single location booking.',
    direction:
      'AI-first, but camera-honest. The creative direction was grounded in one non-negotiable: the outputs had to look like they had been shot on location. Not rendered. Not illustrated. Photographed.\n\nEach scene was built to reflect real operational environments — the lighting, the surface materials, the ambient context of a forecourt canopy or a convenience store aisle. The screens were integrated into each environment with accurate perspective, realistic reflections and appropriate ambient light falloff. The goal was visuals that could sit inside a sales deck or product catalogue without anyone pausing to question whether they were real.',
    execution:
      'AI-assisted visualisation workflow developed from scratch — scene generation, product compositing, environmental context and final catalogue layout. Products were visualised across four primary environment types: petrol forecourt, convenience store, retail interior and outdoor OOH signage.\n\nEach visual was refined through multiple passes to achieve photographic believability — perspective accuracy, shadow and reflection consistency, and material surface fidelity. Output was structured as a reusable asset library, formatted for catalogue, digital, and sales presentation use.',
    results:
      'A full library of premium, catalogue-ready product visuals — created without a single location photoshoot. The workflow is repeatable and scalable: new products, new environments and new formats can be visualised rapidly using the same system. Sales presentation quality improved immediately, and go-to-market timelines were accelerated significantly.',
    impact: [
      'Eliminated the need for expensive location photography across multiple environment types',
      'Created professional catalogue-ready product visuals from scratch',
      'Showed digital screen products in real-world environments — forecourts, retail, OOH',
      'Improved sales presentation quality and commercial credibility',
      'Built a reusable marketing asset system scalable to new products and markets',
      'Accelerated product communication and go-to-market timelines',
    ],
    tags: ['AI Visualisation', 'Product Marketing', 'Digital Signage', 'Catalogue Design', 'Creative Direction'],
  },

];

